import { useState } from 'react';

import type { PresenceSide, Role } from '@/api/types';
import { t, useT } from '@/i18n';
import { formatAgo, formatCountdown, formatDateTime } from '@/lib/format';
import styles from './ChatDetails.module.css';
import shared from '@/pages/pages.module.css';

import { Avatar } from './Avatar';
import chat from './chat.module.css';
import { sideName } from './ChatMessage';
import { ChevronDownIcon, LockIcon } from './Icons';
import { NewBridgeLink } from './NewBridgeLink';
import type { ChatDetailsProps } from './ChatDetails.types';

/** The other side's state in a few words. */
function presenceText(side: PresenceSide, now: number): string {
  if (side.online) return t('chatDetails.online');
  if (side.changedAt === null) return t('chatDetails.notYet');
  return t('chatDetails.away', { ago: formatAgo(side.changedAt, now) });
}

/**
 * What the guide gives way to once a chat is under way, top to bottom: that
 * it is end-to-end encrypted; who is on the other side — their device and
 * whether their page is open now (a green dot) or closed (grey); how long the
 * chat has left; who can still join; and the way to a bridge of your own.
 * Nothing here repeats the chat's head.
 */
export function ChatDetails({ view, now, purpose, presence }: ChatDetailsProps): React.JSX.Element {
  useT();
  const created = new Date(view.createdAt).getTime();
  const expires = new Date(view.expiresAt).getTime();
  const left = Math.min(1, Math.max(0, (expires - now) / Math.max(1, expires - created)));
  const seatsLeft = Math.max(0, view.maxViews - view.views);
  const ownDevices = view.role === 'owner' && purpose === 'receive';
  const title = ownDevices ? t('chatDetails.titleChannel') : t('chatDetails.titleChat');
  const otherRole: Role = view.role === 'owner' ? 'guest' : 'owner';
  const other = presence?.[otherRole] ?? null;
  const otherName = sideName(view.role === 'owner' ? (ownDevices ? 'device' : 'guest') : 'host');
  // under the chat on phones, tablets and narrow windows: folded to its title so the chat stays compact
  const [open, setOpen] = useState(false);

  return (
    <aside className={[shared.guide, shared.details].join(' ')} aria-label={title} data-open={open} data-testid="chat-details">
      {/* the title; on narrow screens it folds and unfolds the details, in a wide window they are always shown */}
      <button
        type="button"
        className={shared.detailsToggle}
        aria-expanded={open}
        onClick={() => {
          setOpen((was) => !was);
        }}
        data-testid="chat-details-toggle"
      >
        <span className={shared.guideTitle}>{title}</span>
        <ChevronDownIcon size={18} className={shared.detailsChevron} />
      </button>
      <div className={styles.detailsSafe}>
        <span className={styles.detailsSafeMark} aria-hidden="true">
          <LockIcon size={16} />
        </span>
        <span>
          <strong className={styles.detailsSafeTitle}>{t('chatDetails.e2e')}</strong>
          <span className={styles.detailsHint}>{t('chatDetails.e2eHint')}</span>
        </span>
      </div>
      <dl className={styles.detailsList}>
        {other ? (
          <div className={styles.detailsRow} data-testid="other-side">
            <dt>{ownDevices ? t('chatDetails.yourOtherDevice') : t('chatDetails.otherSide')}</dt>
            <dd className={styles.detailsWho}>
              <span className={styles.detailsFace}>
                <Avatar seed={`${view.id}:${otherRole}`} />
                <i className={chat.presenceDot} data-online={other.online} aria-hidden="true" />
              </span>
              <span className={styles.detailsWhoText}>
                <strong className={styles.detailsValue}>{other.device ?? otherName}</strong>
                <span className={[styles.detailsHint, other.online ? styles.detailsOnline : ''].join(' ')} data-testid="other-presence">
                  {presenceText(other, now)}
                </span>
              </span>
            </dd>
          </div>
        ) : null}
        <div className={styles.detailsRow}>
          <dt>{t('chatDetails.closesIn')}</dt>
          <dd>
            <strong className={styles.detailsValue}>{formatCountdown(view.expiresAt, now)}</strong>
            <span className={styles.detailsBar} aria-hidden="true">
              <span style={{ width: `${String(Math.round(left * 100))}%` }} />
            </span>
            <span className={styles.detailsHint}>{t('chatDetails.thenErased', { date: formatDateTime(view.expiresAt) })}</span>
          </dd>
        </div>
        <div className={styles.detailsRow}>
          <dt>{t('chatDetails.joined')}</dt>
          <dd>
            <strong className={styles.detailsValue}>
              {t('chatDetails.joinedCount', { joined: view.views, max: view.maxViews })}
            </strong>
            <span className={styles.detailsHint}>{seatsLeft === 0 ? t('chatDetails.linkClosed') : t('chatDetails.canJoin', { n: seatsLeft })}</span>
          </dd>
        </div>
      </dl>
      <div className={styles.detailsFoot}>
        <NewBridgeLink own={view.role !== 'owner'} />
      </div>
    </aside>
  );
}
